// CORE IMPORTS
import { useEffect, useReducer, useRef, useMemo } from "react";

// MATERIAL UI
import { Box, Grid, LinearProgress, Stack } from "@mui/material";

// AMPLITUDE
import { trackEvent } from "src/Amplitude";

// COMPONENTS
import CoinButtonGroup from "src/components/coins/CoinButtonGroup";
import CoinHeader from "src/components/coins/CoinHeader";
import CoinPermissions from "src/components/coins/CoinPermissions";
import CoinProfileComponent from "src/components/coins/CoinProfileComponent";
import CoinsNewsComponent from "src/components/coins/CoinsNewsComponent";
import Notes from "src/components/coins/Notes";
import RecentTransactions from "src/components/coins/RecentTransactions";
import Title from "src/components/coins/Title";
import ToolBox from "src/components/coins/ToolBox";
import TradeAndTargetButtons from "src/components/coins/TradeAndTargetButtons";
import TradingView from "src/components/coins/TradingView";
import NewsSkeletons, { CoinPerformanceSkeleton } from "src/components/common/Skeletons";
import NoFooterLayout from "src/components/core/NoFooterLayout";

// REDUX
import api from "src/store/features/api";
import {
  selectedCoinToken,
  setCandleChartState,
  setCoinStatistics,
} from "src/store/features/markets.slice";
import { useAppDispatch } from "src/store/store";

// HOOKS
import { getTicker } from "src/utils/hooks/useGetTicker";

export default function CoinV2() {
  const [useDefaultTheme, toggle] = useReducer((theme: any) => !theme, true);

  const symbol = useMemo(() => getTicker(), [window.location.href]);

  const dispatch = useAppDispatch();

  // prevents incrementing coin visits on re-render - we only track the first visit on initial page load
  const initialLoadRef = useRef(false);

  // Get coin page data
  const {
    data: coinData,
    isSuccess,
    isFetching: coinFetching,
  } = api.useGetCoinPageDataQuery(
    {
      token: symbol,
    },
    {
      pollingInterval: 60000,
    },
  );

  // Get News data
  const { data: newsData, isLoading } = api.useGetTokenCryptoNewsQuery({
    token: symbol,
  });

  // Get Coin data
  const { data: candleData } = api.useGetCandleChartDataQuery({
    token: symbol,
    interval: "1D",
  });

  // Increment coin visits for user
  const [incrementCoinVisits] = api.useIncrementCoinVisitMutation();

  useEffect(() => {
    if (coinData) {
      dispatch(selectedCoinToken(coinData.currencyConfig?.code ?? "Not Available"));
      dispatch(
        setCoinStatistics({
          price: coinData?.price,
          change: coinData?.change,
          marketCap: coinData?.marketCap,
          tradingVolume: coinData?.tradingVolume,
          supply: coinData?.supply,
          supplyRatio: coinData?.supplyRatio,
          description: coinData?.description,
          currencyConfig: coinData?.currencyConfig,
          rank: coinData?.rank,
        }),
      );

      //for debugging purposes
      if (!coinData?.marketCap) {
        console.error("Coin data is missing market cap", coinData);
      }
      trackEvent("Visit_Page", { page: "Coin", symbol });
    }

    if (candleData) {
      const twentyFourHourData = candleData?.slice(-1)[0];
      if (twentyFourHourData) {
        dispatch(setCandleChartState(twentyFourHourData));
      }
    }

    return () => {};
  }, [coinData, candleData]);

  useEffect(() => {
    if (coinData && symbol && !initialLoadRef.current) {
      incrementCoinVisits({
        coinVisitsDto: {
          token: symbol,
        },
      });
      initialLoadRef.current = true;
    }
  }, [coinData, symbol]);

  const treatedCoinName = useMemo(
    () =>
      coinData?.currencyConfig?.name
        ? coinData.currencyConfig.name.substring(0, coinData.currencyConfig.name.indexOf("("))
        : null,
    [coinData?.currencyConfig?.name],
  );

  return (
    <>
      <NoFooterLayout
        toggleTheme={toggle}
        useDefaultTheme={useDefaultTheme}
        back
        eye
        hideFavourites>
        <div style={{ zIndex: "0", padding: "0px 40px" }}>
          <Grid container spacing={2}>
            {coinFetching && (
              <Box sx={{ width: "100%" }}>
                <LinearProgress />
              </Box>
            )}
            <Grid item xs={12} lg={8}>
              {isSuccess ? (
                <>
                  <div style={{ display: "flex", marginTop: "17px" }}>
                    <CoinHeader />
                    <CoinButtonGroup coinData={coinData} />
                  </div>
                  <Grid container display={{ xs: "flex", md: "none" }}>
                    <TradeAndTargetButtons />
                  </Grid>
                  <TradingView currencyConfig={coinData?.currencyConfig} />
                </>
              ) : (
                <CoinPerformanceSkeleton />
              )}
            </Grid>

            {/* Graph side info */}
            <Grid item xs={12} lg={4} mt={"20px"}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  {/* Your Bitcoin Profile */}
                  <Stack direction="row" justifyContent={"space-between"} mb={1}>
                    {treatedCoinName && (
                      <Title text={`Your ${treatedCoinName} Profile`} variant="hard" />
                    )}
                    <Box display={{ xs: "none", lg: "flex" }}>
                      <ToolBox />
                    </Box>
                  </Stack>
                  <CoinProfileComponent />
                  <Grid
                    item
                    xs={12}
                    marginTop={"16px"}
                    marginBottom={"16px"}
                    display={{ md: "flex", lg: "none" }}>
                    <CoinPermissions />
                  </Grid>
                  <Grid
                    sx={{
                      display: { xs: "none", lg: "flex" },
                      flexDirection: "column",
                    }}>
                    <TradeAndTargetButtons />
                    <CoinPermissions />
                  </Grid>
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={12}>
                  {/* Recent Transactions */}
                  <Title text="Recent Transactions" variant="soft" />
                  <RecentTransactions />
                </Grid>
                <Grid item xs={12} sm={6} md={6} lg={12}>
                  {/* Notes */}
                  <Title text="Notes" variant="soft" />
                  <Notes />
                </Grid>
              </Grid>
            </Grid>

            {/* News component */}
            <Grid item xs={12}>
              {/* Recent Transactions */}
              <div style={{ padding: "10px 10px", marginTop: "10px" }}>
                <Title text="News" variant="hard" />
              </div>
              {isLoading ? <NewsSkeletons /> : <CoinsNewsComponent newsData={newsData} />}
            </Grid>
          </Grid>
        </div>
      </NoFooterLayout>
    </>
  );
}
